// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// COB-260: a deliberately tiny, safe Markdown-subset parser for the
// ProductCo Terms & Conditions body. The terms are served as Markdown by
// productco-kyc; rather than pull in a Markdown dependency (bundle weight +
// a third-party licence entry + the XSS surface of `innerHTML`) we parse the
// small subset the terms document actually uses — headings, paragraphs,
// unordered lists, blockquotes, and inline **bold** — into a structured block
// list. `terms-modal.tsx` renders those blocks as Preact vnodes, so no raw
// HTML is ever injected; the parser has no DOM dependency and is unit-tested
// in isolation.
//
// Unsupported Markdown (tables, ordered lists, code fences, images, inline
// links, emphasis with `_`/`*`) degrades to literal text rather than
// rendering markup — acceptable because the terms content is git-tracked and
// authored against exactly this subset.

/** An inline text run — a contiguous span that is either bold or plain. */
export interface Inline {
  text: string;
  bold: boolean;
}

/** A block-level element of the parsed terms document. `heading.level` is
 *  the Markdown heading depth (1–3, deeper levels clamped to 3); the
 *  renderer maps it to a DOM heading that nests under the modal's own
 *  `<h2>` title. */
export type TermsBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; inlines: Inline[] }
  | { kind: 'paragraph'; inlines: Inline[] }
  | { kind: 'list'; items: Inline[][] }
  | { kind: 'quote'; inlines: Inline[] };

/** Split a line of text into bold / plain runs on `**` delimiters. Segments
 *  alternate plain/bold starting from plain; empty segments are dropped so a
 *  leading `**` does not emit an empty run. An unmatched trailing `**` leaves
 *  its remainder bold — acceptable degradation for controlled content. */
export function parseInline(text: string): Inline[] {
  const parts = text.split('**');
  const inlines: Inline[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (parts[i] === '') continue;
    inlines.push({ text: parts[i], bold: i % 2 === 1 });
  }
  return inlines;
}

/**
 * Parse the terms Markdown body into a flat list of block elements.
 *
 * Line-oriented: blank lines separate blocks; consecutive plain lines are
 * soft-joined into one paragraph (Markdown soft-wrap); `> ` lines join into
 * one blockquote; `- `/`* ` lines each start a list item, and a following
 * indented continuation line is appended to the current item.
 */
export function parseTermsMarkdown(md: string): TermsBlock[] {
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  const blocks: TermsBlock[] = [];

  let para: string[] = [];
  let quote: string[] = [];
  let listItems: string[] | null = null;

  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: 'paragraph', inlines: parseInline(para.join(' ')) });
      para = [];
    }
  };
  const flushQuote = () => {
    if (quote.length) {
      blocks.push({ kind: 'quote', inlines: parseInline(quote.join(' ')) });
      quote = [];
    }
  };
  const flushList = () => {
    if (listItems && listItems.length) {
      blocks.push({ kind: 'list', items: listItems.map(parseInline) });
    }
    listItems = null;
  };
  const flushAll = () => {
    flushPara();
    flushQuote();
    flushList();
  };

  for (const raw of lines) {
    const trimmed = raw.trim();

    if (trimmed === '') {
      flushAll();
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushAll();
      const level = Math.min(heading[1].length, 3) as 1 | 2 | 3;
      blocks.push({ kind: 'heading', level, inlines: parseInline(heading[2]) });
      continue;
    }

    if (trimmed.startsWith('>')) {
      flushPara();
      flushList();
      quote.push(trimmed.replace(/^>\s?/, ''));
      continue;
    }

    const listItem = /^[-*]\s+(.*)$/.exec(trimmed);
    if (listItem) {
      flushPara();
      flushQuote();
      if (!listItems) listItems = [];
      listItems.push(listItem[1]);
      continue;
    }

    // A non-marker, non-blank line. If we are mid-list it is a wrapped
    // continuation of the current item; otherwise it extends a paragraph
    // (flushing any open blockquote first — a plain line ends a quote).
    if (listItems) {
      listItems[listItems.length - 1] += ` ${trimmed}`;
      continue;
    }
    flushQuote();
    para.push(trimmed);
  }

  flushAll();
  return blocks;
}
